import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../i18n";

export default function QuoteForm({ content }) {
  const { t, lang } = useLang();
  const services = content.services || [];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quote", form);
      setDone(true);
      toast.success(t.quote.success);
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-black border border-white/15 px-4 py-3 text-white font-body focus:outline-none focus:border-primary-red focus:ring-2 focus:ring-[#E10600]/30 transition-colors";

  return (
    <section id="quote" className="relative z-10 bg-[#050505] py-24 sm:py-32 border-t border-white/10">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="mb-12 max-w-2xl">
          <div className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary-red mb-4">/ {t.quote.kicker}</div>
          <h2 className="font-display uppercase text-4xl sm:text-6xl text-white leading-[0.95]">{t.quote.title}</h2>
          <p className="mt-5 font-body text-lg text-neutral-400">{t.quote.sub}</p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="border border-primary-red/40 bg-primary-red/5 p-10 flex flex-col items-center text-center"
            data-testid="quote-success"
          >
            <CheckCircle2 size={48} className="text-primary-red mb-4" />
            <p className="font-display uppercase text-2xl text-white">{t.quote.success}</p>
            <button onClick={() => setDone(false)} className="mt-6 font-mono2 text-xs uppercase text-primary-red hover:underline">
              {t.quote.submit} ↺
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={submit}
            className="grid sm:grid-cols-2 gap-4"
            data-testid="quote-form"
          >
            <input className={inputCls} placeholder={t.quote.name} value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="quote-name" />
            <input className={inputCls} placeholder={t.quote.phone} value={form.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="quote-phone" />
            <input className={inputCls} type="email" placeholder={t.quote.email} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-email" />
            <select className={inputCls} value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="quote-service">
              <option value="">{t.quote.selectService}</option>
              {services.map((s, i) => (
                <option key={s.id || i} value={lang === "en" ? s.name_en : s.name_es}>
                  {lang === "en" ? s.name_en : s.name_es}
                </option>
              ))}
            </select>
            <textarea className={`${inputCls} sm:col-span-2`} rows={4} placeholder={t.quote.message} value={form.message} onChange={(e) => set("message", e.target.value)} required data-testid="quote-message" />
            <button
              type="submit"
              disabled={sending}
              className="sm:col-span-2 flex items-center justify-center gap-2 bg-primary-red text-white font-body font-bold uppercase tracking-wide py-4 hover:bg-[#FF1E16] transition-colors disabled:opacity-60"
              data-testid="quote-submit"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? t.quote.sending : t.quote.submit}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
}
